// 개요 패널 — 실시간 통계(/admin/overview) + 파이프라인 + 정책 카드 + 활동 + cURL
import { useEffect, useState } from 'react';
import { useTab } from '../lib/tabStore';
import { useScanStore } from '../lib/scanStore';
import { usePolicyStore } from '../lib/policyStore';
import { adminGet } from '../lib/adminApi';
import { renderSafeHtml } from '../lib/sanitize';
import type { Policy } from '../lib/policyStore';

// API 없을 때 보여줄 데모 통계 (total=142, safe=137 → 96.5%)
const SEED_STATS = { total: 142, safe: 137, quarantined: 3, blocked: 2, failed: 0, pending: 0 };

const STEPS = [
  { num: 'STEP 1', name: '정책 필터', desc: '확장자·용량·MIME 타입·파일 헤더 검증' },
  { num: 'STEP 2', name: '해시 조회', desc: 'SHA256 블랙리스트 DB 대조 (밀리초)' },
  { num: 'STEP 3', name: 'ClamAV 스캔', desc: '시그니처 기반 악성코드 탐지' },
  { num: 'STEP 4', name: 'YARA 룰', desc: '매크로·임베드·패턴 분석' },
  { num: 'STEP 5', name: '웹훅 콜백', desc: '결과를 호출 프로젝트에 전달' },
];

interface OverviewStats {
  stats: { total: number; safe: number; quarantined: number; blocked: number; failed: number; pending: number };
}
interface DbPolicy { id: string; name: string; maxSizeMb: number; maxFiles: number; allowedExt: string[] | null; engines: string[] | null }

export function Overview() {
  const setTab = useTab((s) => s.setTab);
  const liveActivity = useScanStore((s) => s.activityEntries);
  const storePolicies = usePolicyStore((s) => s.policies);
  const [stats, setStats] = useState<OverviewStats['stats']>(SEED_STATS);
  const [statsErr, setStatsErr] = useState(false);
  const [dbPolicies, setDbPolicies] = useState<DbPolicy[]>([]);

  useEffect(() => {
    let alive = true;
    const load = () => Promise.all([
      adminGet<OverviewStats>('/admin/overview').catch(() => null),
      adminGet<{ items: DbPolicy[] }>('/admin/policies').catch(() => null),
    ]).then(([ov, pol]) => {
      if (!alive) return;
      if (ov) { setStats(ov.stats); setStatsErr(false); } else setStatsErr(true);
      if (pol) setDbPolicies(pol.items);
    });
    load();
    const id = setInterval(load, 10_000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const displayStats = stats;
  const passRate = displayStats.total > 0
    ? ((displayStats.safe / displayStats.total) * 100).toFixed(1)
    : '0.0';

  // API 정책 없으면 policyStore 폴백 (테스트/초기 로딩)
  const displayPolicies: DbPolicy[] = dbPolicies.length > 0
    ? dbPolicies
    : Object.entries(storePolicies).map(([id, p]) => ({
        id,
        name: p.name,
        maxSizeMb: p.maxSizeMB,
        maxFiles: p.maxFiles,
        allowedExt: p.allowedExt,
        engines: p.engines,
      }));

  const recentActivity = liveActivity.slice(0, 5);

  return (
    <>
      <div className="arch-flow">
        <div className="arch-title">⚡ 파일 검사 파이프라인</div>
        <div className="arch-pipeline">
          {STEPS.map((s) => (
            <div className="arch-step" key={s.num}>
              <div className="arch-step-num">{s.num}</div>
              <div className="arch-step-name">{s.name}</div>
              <div className="arch-step-desc">{s.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {statsErr && (
        <div style={{ fontSize: 12, color: 'var(--yellow)', marginBottom: 8, fontFamily: 'JetBrains Mono, monospace' }}>
          ⚠ 백엔드 연결 실패 — 이전 값 표시 중
        </div>
      )}

      <div className="stats">
        <div className="stat green">
          <div className="stat-label">오늘 검사</div>
          <div className="stat-val">{displayStats.total}</div>
          <div className="stat-sub">총 검사 건수</div>
        </div>
        <div className="stat blue">
          <div className="stat-label">통과율</div>
          <div className="stat-val">{passRate}%</div>
          <div className="stat-sub">{displayStats.safe}개 안전 통과</div>
        </div>
        <div className="stat red">
          <div className="stat-label">격리 처리</div>
          <div className="stat-val">{displayStats.quarantined}</div>
          <div className="stat-sub">운영자 확인 대기</div>
        </div>
        <div className="stat yellow">
          <div className="stat-label">정책 차단</div>
          <div className="stat-val">{displayStats.blocked}</div>
          <div className="stat-sub">확장자/용량 위반</div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 20 }}>
        <div className="card">
          <div className="card-head">
            <div className="card-head-title">🎯 등록된 정책</div>
            <button className="btn btn-ghost btn-sm" onClick={() => setTab('policies')}>관리</button>
          </div>
          <div style={{ padding: 0 }}>
            {displayPolicies.map((p, i) => (
              <div
                key={p.id}
                style={{ padding: '14px 20px', borderBottom: i === displayPolicies.length - 1 ? 'none' : '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{p.name}</div>
                  <div className="text-mono" style={{ color: 'var(--text3)', marginTop: 3 }}>policy_id: {p.id}</div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <span className="ext-chip">{p.maxSizeMb}MB</span>
                  <span className="ext-chip">{p.maxFiles} files</span>
                  <span className="ext-chip">{(p.allowedExt ?? []).length} ext</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <div className="card-head-title">📡 최근 활동</div>
            <button className="btn btn-ghost btn-sm" onClick={() => setTab('activity')}>전체보기</button>
          </div>
          <div style={{ padding: 0 }}>
            {recentActivity.map((a, i) => (
              <div key={i} className="log-item" style={i === recentActivity.length - 1 ? { borderBottom: 'none' } : undefined}>
                <div className="log-time">{a.time}</div>
                <div className={`log-dot ld-${a.type}`} />
                <div className="log-text">{renderSafeHtml(a.html)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="card mt-20">
        <div className="card-head">
          <div className="card-head-title">🚀 빠른 시작 (cURL)</div>
          <button className="btn btn-ghost btn-sm" onClick={() => setTab('api')}>전체 API</button>
        </div>
        <div className="code-block">
{`# 1) 파일 검사 요청
curl -X POST https://fileguard.local/v1/scan \\
  -H "X-API-Key: fg_live_xxxxxxxx" \\
  -F "file=@suspicious.pdf" \\
  -F "policy_id=default" \\
  -F "callback_url=https://my-app.com/webhook"

# 즉시 응답 (비동기)
{
  "file_id": "f_a3f8b2c1d9e4",
  "status": "pending",
  "queue_position": 3
}

# 2) 검사 완료 시 webhook으로 결과 전달됨
{
  "file_id": "f_a3f8b2c1d9e4",
  "status": "safe",
  "engines": ["clamav", "yara"],
  "scan_time_ms": 1240
}`}
        </div>
      </div>
    </>
  );
}
